import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Package, Plus, MapPin, Calendar, Loader2, LogOut } from "lucide-react";

interface TransportRequest {
  id: string;
  sender_name: string;
  departure_city: string;
  departure_country: string;
  arrival_city: string;
  arrival_country: string;
  package_type: string;
  status: string;
  created_at: string;
  preferred_date: string | null;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800" },
  confirmed: { label: "Confirmé", color: "bg-blue-100 text-blue-800" },
  in_transit: { label: "En transit", color: "bg-purple-100 text-purple-800" },
  delivered: { label: "Livré", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Annulé", color: "bg-red-100 text-red-800" },
};

const MyRequests = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<TransportRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from("transport_requests")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (!error && data) {
        setRequests(data);
      }
      setIsLoading(false);
    };

    if (user) {
      fetchRequests();
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 section-padding">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Mes demandes de transport
              </h1>
              <p className="text-muted-foreground">
                Suivez l'état de vos demandes en temps réel
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link to="/demande-transport">
                  Nouvelle demande
                </Link>
              </Button>
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="w-5 h-5 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>

          {/* Requests List */}
          {requests.length === 0 ? (
            <div className="bg-card border border-border rounded-3xl p-8 md:p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                <Package className="w-10 h-10 text-secondary" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                Vous n'avez pas encore soumis de demande de transport
              </h2>
              <p className="text-muted-foreground mb-6">
                Créez votre première demande pour envoyer un colis
              </p>
              <Button asChild variant="outline">
                <Link to="/demande-transport">
                  <Plus className="w-5 h-5 mr-2" />
                  Faire une demande
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => {
                const status = statusLabels[request.status] || statusLabels.pending;
                return (
                  <div
                    key={request.id}
                    className="bg-card border border-border rounded-2xl p-6 hover:border-secondary/50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                            {status.label}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {request.package_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-foreground font-medium mb-2">
                          <MapPin className="w-4 h-4 text-secondary" />
                          <span>{request.departure_city}, {request.departure_country}</span>
                          <span className="text-muted-foreground">→</span>
                          <span>{request.arrival_city}, {request.arrival_country}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Créée le {new Date(request.created_at).toLocaleDateString("fr-FR")}
                            </span>
                          </div>
                          {request.preferred_date && (
                            <div className="flex items-center gap-1">
                              <span>Date souhaitée: {request.preferred_date}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyRequests;
